import { useState, useEffect } from 'react';

/**
 * Custom hook for debouncing values.
 * Returns a debounced version of the value that only updates after the specified delay.
 *
 * Useful for preventing rapid API calls when user input changes quickly.
 *
 * @param {*} value - The value to debounce
 * @param {number} delay - Delay in milliseconds
 * @returns {*} The debounced value
 *
 * @example
 * const debouncedSearchTerm = useDebounce(searchTerm, 300);
 * useEffect(() => {
 *   // This only runs 300ms after searchTerm stops changing
 *   fetchResults(debouncedSearchTerm);
 * }, [debouncedSearchTerm]);
 */
const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => clearTimeout(timer);
  }, [value, delay]);

  return debouncedValue;
};

export default useDebounce;
